import React, { useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "~/components/Button";
import { Input } from "~/components/Controls/Input";
import { useConfirm, useMessage } from "~/components/Dialogs";
import { IntentText } from "~/components/Form";
import { Progress } from "~/components/Progress";
import { TableRowActions, TableSort } from "~/components/Table";
import { Intent } from "~/lib/intent";
import { sortByString } from "~/lib/sort";
import { clientsLinks } from "~/modules/Clients";
import { createAgencyAccountManager, deleteAgencyAccountManager, updateAgencyAccountManager } from "~/modules/Clients/api";
import { IAccountManager } from "~/modules/Clients/models/AccountManager";
import { DictionaryUsageLinks } from "~/modules/Dictionary/components/DictionaryUsageLinks";
import { AGENCY_ACCOUNT_MANAGERS_QUERY_KEY, useAgencyAccountManagersQuery } from "~/modules/Dictionary/queries";
import style from "./AgencyAccountManagersTab.module.scss";

const getErrorMessage = (error: any): string => error?.response?.data?.message || error?.message || "Unknown error";

type FormMode = "idle" | "create" | "edit";

export const AgencyAccountManagersTab = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const message = useMessage();

  const { data: items = [], isPending, isFetching, isError, error, refetch } = useAgencyAccountManagersQuery();

  const [mode, setMode] = useState<FormMode>("idle");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [slackUserId, setSlackUserId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const createMutation = useMutation({
    mutationFn: () => createAgencyAccountManager({ name: name.trim(), slackUserId: slackUserId.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AGENCY_ACCOUNT_MANAGERS_QUERY_KEY });
      resetForm();
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      updateAgencyAccountManager(editingId!, { name: name.trim(), slackUserId: slackUserId.trim() || undefined }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AGENCY_ACCOUNT_MANAGERS_QUERY_KEY });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: IAccountManager["id"]) => deleteAgencyAccountManager(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: AGENCY_ACCOUNT_MANAGERS_QUERY_KEY });
    },
  });

  const normalizedItems = useMemo(() => items ?? [], [items]);
  const isBusy = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;
  const isFormOpen = mode !== "idle";
  const isEditing = mode === "edit";
  // A Slack id is required on create; on update an empty field keeps the stored id.
  const canSubmit = name.trim().length > 0 && (isEditing || slackUserId.trim().length > 0);

  const resetForm = () => {
    setFormError(null);
    setName("");
    setSlackUserId("");
    setEditingId(null);
    setMode("idle");
  };

  const handleOpenAdd = () => {
    setFormError(null);
    setName("");
    setSlackUserId("");
    setEditingId(null);
    setMode("create");
  };

  const handleEdit = (item: IAccountManager) => {
    setFormError(null);
    setName(item.name);
    setSlackUserId(item.slackUserId ?? "");
    setEditingId(item.id);
    setMode("edit");
  };

  const handleCancel = () => resetForm();

  const handleSave = () => {
    setFormError(null);
    const mutation = isEditing ? updateMutation : createMutation;
    mutation.mutate(undefined, {
      onError: (e: any) => {
        const msg = getErrorMessage(e);
        setFormError(msg);
        message(msg, { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
      },
    });
  };

  const handleDelete = (item: IAccountManager) =>
    confirm(
      <div>
        <div>{`Delete account manager "${item.name}"?`}</div>
        <div style={{ marginTop: 8, fontSize: '0.9em' }}>
          Before deleting, make sure this manager is not linked to any agencies.{' '}
          <NavLink to={`${clientsLinks.agencies()}?am=${item.id}`} target="_blank">
            Check agencies
          </NavLink>
        </div>
      </div>,
      {
        icon: <i className="fa fa-question" />,
        intent: Intent.DANGER,
        onOk: () => {
          deleteMutation.mutate(item.id, {
            onError: (e: any) => {
              message(getErrorMessage(e), { intent: Intent.DANGER, icon: <i className="fa fa-times" /> });
            },
          });
        },
      }
    );

  return (
    <div>
      <div className={style.toolbar}>
        <div className={style.toolbarLeft}>
          {isFormOpen && (
            <div className={style.fields}>
              <Input
                className={style.field}
                placeholder="Name"
                value={name}
                onChange={setName}
                disabled={isBusy}
                autoFocus
              />
              <Input
                className={style.field}
                placeholder={isEditing ? "Slack user ID (U0ABC12DE)" : "Slack user ID (U0ABC12DE), required"}
                value={slackUserId}
                onChange={setSlackUserId}
                disabled={isBusy}
              />
            </div>
          )}
          {!isFormOpen && <DictionaryUsageLinks links={[{ label: "Agencies", to: clientsLinks.agencies() }]} />}
        </div>
        <div className={style.toolbarRight}>
          {!isFormOpen ? (
            <Button
              responsive
              intent={Intent.PRIMARY}
              disabled={isBusy}
              onClick={handleOpenAdd}
              leftIcon={<i className="fa fa-plus" />}
            >
              Add
            </Button>
          ) : (
            <>
              <Button
                responsive
                intent={Intent.PRIMARY}
                disabled={!canSubmit || isBusy}
                onClick={handleSave}
                leftIcon={<i className="fa fa-check" />}
              >
                {isEditing ? "Update" : "Save"}
              </Button>
              <Button responsive disabled={isBusy} onClick={handleCancel} leftIcon={<i className="fa fa-times" />}>
                Cancel
              </Button>
            </>
          )}
          <Button
            responsive
            intent={Intent.SUCCESS}
            disabled={isFetching}
            onClick={() => refetch()}
            leftIcon={<i className="fa fa-refresh" />}
          >
            Reload
          </Button>
        </div>
      </div>

      {isFormOpen && formError && (
        <IntentText className={style.error} intent={Intent.DANGER}>
          {formError}
        </IntentText>
      )}
      {isError && (
        <IntentText className={style.error} intent={Intent.DANGER}>
          {getErrorMessage(error)}
        </IntentText>
      )}

      <div className={style.tableWrap}>
        {isPending && normalizedItems.length === 0 && <Progress message="Loading..." />}
        {!isPending && normalizedItems.length === 0 && (
          <div className={style.emptyState}>No agency account managers.</div>
        )}
        {normalizedItems.length > 0 && (
          <TableSort<IAccountManager>
            records={normalizedItems}
            header
            columns={[
              { header: "Name", field: "name", sort: sortByString, cellAttrs: { className: style.nameCell } },
              { header: "Slack user ID", field: "slackUserId" },
              {
                short: true,
                cell: (m) => (
                  <TableRowActions>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.PRIMARY}
                      disabled={isBusy}
                      onClick={() => handleEdit(m)}
                      leftIcon={<i className="fa fa-edit" />}
                    >
                      Edit
                    </Button>
                    <Button
                      small
                      variant="menu"
                      intent={Intent.DANGER}
                      disabled={isBusy}
                      onClick={() => handleDelete(m)}
                      leftIcon={<i className="fa fa-trash" />}
                    >
                      Delete
                    </Button>
                  </TableRowActions>
                ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
};
